import { Breadcrumb, Divider, Layout, theme, Row, Col, Spin, Alert, Select, DatePicker, Button } from "antd";
import { Content } from "antd/es/layout/layout";
import { ArrowLeftOutlined } from "@ant-design/icons";
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SalesCard from '../Home/components/SalesCard/SalesCard';
import SalesComparisonChart from '../Home/components/SalesComparisonChart/SalesComparisonChart';
import ProductMixChart from '../Home/components/ProductMixChart/ProductMixChart';
import EvolutionTable from '../Home/components/EvolutionTable/EvolutionTable';

import {
  apiExpectedReceivablesByDate,
  apiExpectedReceivablesByDateTotal,
  apiExpectedReceivablesByDateNext,
  apiExpectedReceivablesByProduct,
  apiAccountExpectedReceivablesComparison
} from '../../../api/receivableMetrics';
import { format, subDays, startOfMonth, subMonths } from 'date-fns';
import './ReceivableDashboard.scss';
import dayjs from "dayjs";

const ReceivableDashboard = () => {
  const navigate = useNavigate();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const [totalReceivables, setTotalReceivables] = useState(0);
  const [nextReceivable, setNextReceivable] = useState({ date: null, total: 0 });
  const [currentPeriodData, setCurrentPeriodData] = useState([]);
  const [previousPeriodData, setPreviousPeriodData] = useState([]);
  const [productMix, setProductMix] = useState([]);
  const [evolutionData, setEvolutionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evolutionLoading, setEvolutionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewType, setViewType] = useState('daily');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [customDateRange, setCustomDateRange] = useState([dayjs(subDays(new Date(), 7)), dayjs(new Date())]);

  useEffect(() => {
    if (viewType === 'daily') {
      const today = new Date();
      let startDate, endDate;
      endDate = today;
      startDate = subDays(today, 7);
      setCustomDateRange([dayjs(startDate), dayjs(endDate)]);
    } else if (viewType === 'monthly') {
      const today = new Date();
      let startDate, endDate;
      endDate = today;
      startDate = startOfMonth(today);
      setCustomDateRange([dayjs(startDate), dayjs(endDate)]);
    }
  }, [viewType]);

  const fetchTotalReceivables = useCallback(async () => {
    try {
      setError(null);
      const response = await apiExpectedReceivablesByDateTotal(selectedAccount?.id);
      setTotalReceivables(parseFloat(response.total || 0) / 100);
    } catch (err) {
      console.error('Error fetching total receivables:', err);
      setError('Erro ao carregar total de recebíveis');
    }
  }, [selectedAccount]);

  const fetchNextReceivable = useCallback(async () => {
    try {
      setError(null);
      const response = await apiExpectedReceivablesByDateNext(selectedAccount?.id);
      setNextReceivable({
        date: response.date,
        total: parseFloat(response.total || 0) / 100
      });
    } catch (err) {
      console.error('Error fetching next receivable:', err);
      setError('Erro ao carregar próximo recebimento');
    }
  }, [selectedAccount]);

  const fetchCurrentPeriodData = useCallback(async () => {
    try {
      setError(null);
      const filters = {
        date_after: format(customDateRange[0].toDate(), 'yyyy-MM-dd'),
        date_before: format(customDateRange[1].toDate(), 'yyyy-MM-dd'),
      };

      const response = await apiExpectedReceivablesByDate(filters, selectedAccount?.id);
      const data = response.results || response || [];
      data.reverse();
      setCurrentPeriodData(data);
    } catch (err) {
      console.error('Error fetching current period receivables:', err);
      setError('Erro ao carregar dados do período atual');
    }
  }, [customDateRange, selectedAccount]);

  const fetchPreviousPeriodData = useCallback(async () => {
    try {
      setError(null);

      let today = new Date();
      let startDate, endDate;
      if (viewType === 'monthly') {
        const lastMonth = subMonths(today, 1);
        const lastMonthStart = startOfMonth(lastMonth);
        const currentDay = today.getDate();
        const lastMonthSameDay = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), currentDay);
        startDate = lastMonthStart;
        endDate = lastMonthSameDay;
      } else {
        startDate = customDateRange[0].toDate();
        endDate = customDateRange[1].toDate();
        const diff = endDate.getTime() - startDate.getTime();
        endDate = new Date(startDate.getTime() - 1);
        startDate = new Date(endDate.getTime() - diff);
      }

      const filters = {
        date_before: format(endDate, 'yyyy-MM-dd'),
        date_after: format(startDate, 'yyyy-MM-dd')
      };

      const response = await apiExpectedReceivablesByDate(filters, selectedAccount?.id);
      const data = response.results || response || [];
      data.reverse();
      setPreviousPeriodData(data);
    } catch (err) {
      console.error('Error fetching previous period receivables:', err);
      setError('Erro ao carregar dados do período anterior');
    }
  }, [customDateRange, selectedAccount]);

  const fetchProductMix = useCallback(async () => {
    try {
      setError(null);

      const filters = {
        date_after: format(customDateRange[0].toDate(), 'yyyy-MM-dd'),
        date_before: format(customDateRange[1].toDate(), 'yyyy-MM-dd'),
      };

      const response = await apiExpectedReceivablesByProduct(filters, selectedAccount?.id);
      const data = response.results || response || [];
      setProductMix(data);
    } catch (err) {
      console.error('Error fetching product mix:', err);
      setError('Erro ao carregar mix de produtos');
    }
  }, [customDateRange, selectedAccount]);

  const fetchEvolutionData = useCallback(async () => {
    try {
      setEvolutionLoading(true);
      setError(null);

      const filters = {
        date_before: format(customDateRange[0].toDate(), 'yyyy-MM-dd'),
        date_after: format(customDateRange[1].toDate(), 'yyyy-MM-dd')
      };

      const response = await apiAccountExpectedReceivablesComparison(filters, selectedAccount?.id);
      const evolutionDataResponse = response.results || response || [];
      setEvolutionData(evolutionDataResponse);
    } catch (err) {
      console.error('Error fetching evolution data:', err);
      setError('Erro ao carregar dados de evolução');
    } finally {
      setEvolutionLoading(false);
    }
  }, [customDateRange]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        fetchTotalReceivables(),
        fetchNextReceivable(),
        fetchCurrentPeriodData(),
        fetchPreviousPeriodData(),
        fetchProductMix()
      ]);
      setLoading(false);

      fetchEvolutionData();
    };

    fetchData();
  }, [fetchTotalReceivables, fetchNextReceivable, fetchCurrentPeriodData, fetchPreviousPeriodData, fetchProductMix, fetchEvolutionData]);

  const formatNextDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return ` (${day}/${month}/${year})`;
  };

  return (
    <div className='ReceivableDashboard'>
      <Layout
        style={{
          padding: "1.25rem",
        }}
      >
        <Breadcrumb
          style={{
            margin: "16px 0",
          }}
        >
          <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
            <h2 className="title-page">Resultado Recebíveis</h2>
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <Button
                type="primary"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/')}
                style={{ fontWeight: 600 }}
              >
                Dashboard de Vendas
              </Button>
              <Select
                value={viewType}
                onChange={setViewType}
                style={{ width: 140 }}
                options={[
                  { value: 'daily', label: 'Visão Diária' },
                  { value: 'monthly', label: 'Visão Mensal' },
                  { value: 'custom', label: 'Personalizado' }
                ]}
              />
              {viewType === 'custom' && (
                <DatePicker.RangePicker
                  value={customDateRange}
                  onChange={setCustomDateRange}
                  format={{ format: 'DD/MM/YYYY', type: 'mask' }}
                  style={{ width: 280 }}
                  placeholder={['Data inicial', 'Data final']}
                />
              )}
            </div>
          </div>
          <Divider />
          <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
          <Breadcrumb.Item>Resultado Recebíveis</Breadcrumb.Item>
        </Breadcrumb>
        <Content
          style={{
            padding: 24,
            margin: 0,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          {error && (
            <Alert
              message="Erro ao carregar dados"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}
          <Spin spinning={loading} tip="Carregando dados...">
            <div className="dashboard-container">
              <div className="dashboard-section">
                <Row gutter={[24, 24]} className="sales-row">
                  <Col xs={24} sm={12} lg={12}>
                    <SalesCard
                      title="Total a Receber"
                      value={totalReceivables || 0}
                      prefix="R$"
                      trend={null}
                      loading={loading}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={12}>
                    <SalesCard
                      title={`Próximo Recebimento${formatNextDate(nextReceivable.date)}`}
                      value={nextReceivable.total || 0}
                      prefix="R$"
                      trend={null}
                      loading={loading}
                    />
                  </Col>
                </Row>
              </div>

              <div className="dashboard-section" style={{ marginTop: '24px' }}>
                <Row gutter={[24, 24]}>
                  <Col xs={24} lg={16}>
                    <SalesComparisonChart
                      title={viewType === 'daily'
                        ? "Recebíveis previstos - Comparativo semana a semana"
                        : "Recebíveis previstos - Comparativo mês atual vs mês anterior"
                      }
                      currentWeekData={currentPeriodData}
                      previousWeekData={previousPeriodData}
                      loading={loading}
                    />
                  </Col>
                  <Col xs={24} lg={8}>
                    <ProductMixChart
                      title={viewType === 'daily' ? "Mix de Produtos" : "Mix de Produtos - Mês atual"}
                      productData={productMix}
                      loading={loading}
                    />
                  </Col>
                </Row>
              </div>

              <div className="dashboard-section" style={{ marginTop: '24px' }}>
                <Row gutter={[24, 24]}>
                  <Col xs={24}>
                    <EvolutionTable
                      data={evolutionData}
                      loading={evolutionLoading}
                      showSubBadge={true}
                    />
                  </Col>
                </Row>
              </div>
            </div>
          </Spin>
        </Content>
      </Layout>
    </div>
  );
};

export default ReceivableDashboard;
